/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */
/* eslint-disable max-statements */
/* eslint-disable no-extra-boolean-cast */

import EventEmitter from 'eventemitter3';
import JavascriptStateMachine from 'javascript-state-machine';
import isNil from 'lodash/isNil';

import ErrorName from '../../error/error-name';
import HtmlMediaUtil from '../../html/html-media-util';
import StateMachine from '../../state-machine/state-machine';
import StateMachineFactory from '../../state-machine/state-machine-factory';
import StretchNative from '../../stretch/stretch-native';
import ViewerLifeCycle from '../../viewer/life-cycle/viewer-life-cycle';

// TODO Sdílí spoustu kódu s ostatními ovladači, DRY.
// - TizenSssp4VideoViewerController
// - WebosSignage3_0StreamViewerController
// TODO Sdílí CSS třídu viewer-controller-video s ostatními ovladači.
// - To je nevhodné z pohledu organizace, kde by mělo být jméno dle třídy.
// - Na druhou stranu aktuální přístup, kdy se třídy odebírají a přidávají
//   při každé změně média se také neosvědčil, protože na krátký okamžik nemusí
//   mít video element žádnou třídu, takže se zbytečně mění jeho rozměry.
// - Zatím tedy ponecháno takto, dokud nedojde k refaktorování na lepší řešení.
/**
 * Ovladač pro zobrazování video souborů.
 * @implements {ViewerController}
 */
class ViewerControllerVideo {
  /**
   * Vytvoří instanci.
   * @param {Object} [opts={}]
   * @param {boolean} [opts.genericViewerVideoSeekDisable] Příznak pro zakázané seekování ve videu.
   * @param {string|null} [opts.poster] Poster pro, který se zobrazí při zastaveném videu.
   * @param {boolean} [opts.preloadBlackeningFix] Příznak pro opravu černání.
   * @param {boolean} [opts.preloadBlackeningFixDelay] Hodnota určující zpoždění FIXu opravujícího
   * černání u video elementu (doba zase kterou se nastavní původní hodnoty elementu).
   * @returns {ViewerControllerVideo} Nová instance.
   */
  constructor({
    genericViewerVideoSeekDisable,
    poster,
    preloadBlackeningFix,
    preloadBlackeningFixDelay
  }) {
    /**
     * Custom poster pro video element.
     * @private
     * @type {null|string}
     */
    this.poster = poster;

    /**
     * Příznak, který aktivuje/deaktivuje seekování do konkrétního času videa.
     * @private
     * @type {boolean}
     */
    this.seekDisable = genericViewerVideoSeekDisable;
    /**
     * Příznak, který aktivuje/deaktivuje opravný mechanismus černání.
     * @private
     * @type {boolean}
     */
    this.preloadBlackeningFix = preloadBlackeningFix;

    /**
     * Hodnota určující zpoždění FIXu opravujícího černání u video elementu
     * (doba zase kterou se nastavní původní hodnoty elementu).
     * @private
     * @type {boolean}
     */
    this.preloadBlackeningFixDelay = preloadBlackeningFixDelay;

    this.stretchNative = new StretchNative();
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    /**
     * @private
     * @type {HtmlMediaUtil}
     */
    this.mediaUtil = new HtmlMediaUtil();
    /**
     * Callback pro selhání spouštění videa, pokud nějaké probíhá.
     * @private
     * @type {?Function}
     */
    this.playReject = null;
    this.playSeekReject = null;
    /**
     * Callback pro úspěch spouštění videa, pokud nějaké probíhá.
     * @private
     * @type {?Function}
     */
    this.playResolve = null;
    /**
     * Časovač pro maximální dobu čekání na spuštění přehrávání.
     * @private
     * @type {?number}
     */
    this.playTimeout = null;
    this.positionLastTime = null;
    this.positionTimeout = null;
    this.scheduleInfo = null;
    const stateMachineFactory = new StateMachineFactory(
      JavascriptStateMachine,
      StateMachine
    );
    this.viewerLifeCycle = new ViewerLifeCycle(stateMachineFactory);
    this.viewerSource = null;
    // Navázání naslouchání na instanci.
    this.onCanplaythrough = this.onCanplaythrough.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onError = this.onError.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
    this.error = null;
  }

  /**
   * Naváže naslouchání událostem z elementu.
   * @param {HTMLVideoElement} el Element, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  bindElement(el) {
    el.addEventListener('canplaythrough', this.onCanplaythrough);
    el.addEventListener('ended', this.onEnded);
    el.addEventListener('error', this.onError);
    el.addEventListener('playing', this.onPlaying);
  }

  /** @inheritdoc */
  destroy() {
    this.stopPositionWatchdog();
    clearTimeout(this.playTimeout);
    const { viewerSource } = this;
    if (!isNil(viewerSource)) {
      const el = viewerSource.getEl();
      this.unbindElement(el);
    }
  }

  /** @inheritdoc */
  enter(offset) {
    const { scheduleInfo, viewerSource } = this;
    const viewerFrame = viewerSource.getFrame();
    const viewerFrameEl = viewerFrame.getEl();

    return this.viewerLifeCycle
      .enter(() => {
        // Určení všech potřebných hodnot.
        const volume = scheduleInfo.getVolume();
        const el = viewerSource.getEl();
        // Zobrazení obalujícího elementu.
        viewerFrame.enter();

        // Kontrola jestli se již při preloadu nevyskytla chyba,
        // pokud se chyba vykytla, tak ji deleguji.
        if (this.error) {
          throw this.error;
        }

        // Zapnutí zvuku videa, pouze pokud je potřeba.
        // Zapnutí při volume na 0 může přerušit přehrávání zvuku jiného videa.
        if (volume > 0) {
          el.muted = false;
        }
        // #505 - webOS 6.0 - nefunkční přehrávání videí
        el.style.visibility = 'visible';

        // Pokud je požadaváno seekování videa
        if (!isNil(offset)) {
          return this.playSeek(offset, viewerFrameEl, el);
        }
        // Zahájení přehrávání videa.
        return this.play(viewerFrameEl, el);
      })
      .finally(() => {
        // Kontrola povolení FIXu černání.
        if (Boolean(this.preloadBlackeningFix)) {
          setTimeout(
            () => viewerFrameEl.style.setProperty('opacity', '1.0'),
            this.preloadBlackeningFixDelay
          );
        }
      });
  }

  /** @inheritdoc */
  leave() {
    return this.viewerLifeCycle.leave(() => {
      const { viewerSource } = this;
      // Určení všech potřebných hodnot.
      const el = viewerSource.getEl();
      const viewerFrame = viewerSource.getFrame();
      // Skrytí obalujícího elementu.
      viewerFrame.leave();
      // Video se zastaví.
      el.pause();
    });
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na dostatečné načtení zdroje v elementu.
   * @private
   * @returns {undefined} Nic.
   */
  onCanplaythrough() {
    const el = this.viewerSource.getEl();
    this.emitter.emit('loaded');
    // Ošetření neočekávaného opakovaného vyvolaní události `canplaythrough`.
    // Dochází k němu v každé iteraci přehrávání při nastavení `loop`.
    // Pozorováno ale i jindy, např. při minimalizaci okna prohlížeče.
    el.removeEventListener('canplaythrough', this.onCanplaythrough);
  }

  /**
   * Dosažení konce přehrávání.
   * @private
   * @returns {undefined} Nic.
   */
  onEnded() {
    this.stopPositionWatchdog();
    this.emitter.emit('played');
  }

  /**
   * Reakce na chybu v elementu.
   *
   * Pokud probíhá pokus o spuštění videa,
   * tak se chybová událost interpretuje jako jeho selhání.
   * V opačném případě se chyba emituje.
   * @private
   * @returns {undefined} Nic.
   */
  onError() {
    this.stopPositionWatchdog();
    const el = this.viewerSource.getEl();
    const error = this.mediaUtil.createError(el);
    const { playReject, playSeekReject } = this;
    this.error = error;
    if (!isNil(playSeekReject)) {
      return playSeekReject(error);
    }
    if (!isNil(playReject)) {
      return playReject(error);
    }
    this.emitter.emit('error', error);
  }

  /**
   * Reakce na skutečný začátek přehrávání.
   * Pokud probíhá pokus o spuštění videa,
   * tak se událost se začátkem přehráváním interpretuje jako jeho dokončení.
   * V opačném případě se nedělá nic.
   * @private
   * @returns {undefined} Nic.
   */
  onPlaying() {
    const { playResolve } = this;
    if (!isNil(playResolve)) {
      return playResolve();
    }
  }

  /**
   * Spustí přehrávání video elementu.
   *
   * Vzhledem ke zpětné kompatibilitě se starými Chrome se naslouchá události `playing`,
   * místo spolehání na `Promise` vrácenou metodou `play`.
   *
   * Je nutné naslouchat na událost `playing`, která informuje o skutečném začátku přehrávání.
   * Událost `play` informuje pouze o začátku pokusu o spuštění.
   * Exerimentálně bylo zjištěno, že stejně se chová i `Promise`,
   * která byla vrácena při volání `HTMLMediaElement.play`.
   *
   * Dokumentace:
   * [HTMLMediaElement.play returns a Promise](https://developers.google.com/web/updates/2016/03/play-returns-promise).
   * @param {HTMLVideoElement} viewerFrameEl Rodičovský element, do kterého byl vložen video element.
   * @param {HTMLVideoElement} el Element, který se má přehrát.
   * @private
   * @returns {Promise.<undefined>}
   */
  play(viewerFrameEl, el) {
    // Kontrola povolení FIXu černání.
    if (Boolean(this.preloadBlackeningFix)) {
      viewerFrameEl.style.setProperty('opacity', '0.999');
    }

    return new Promise((resolve, reject) => {
      this.playReject = reject;
      this.playResolve = resolve;
      const promise = el.play();
      if (!isNil(promise)) {
        promise.catch((error) => reject(error));
      }
      this.playTimeout = setTimeout(
        () => reject(new Error('Play request timed out.')),
        // TODO Jakou hodnotu zvolit? Přidat do konfigurace.
        30 * 1000
      );
      // Video currentPosition watchdog init
      this.positionLastTime = 0;
      this.startPositionWatchdog();
    }).finally(() => {
      this.playReject = null;
      this.playResolve = null;
      clearTimeout(this.playTimeout);
    });
  }

  /**
   * Spustí video v daném čase.
   * Pokud je offset menší 1000 a seek není podporovaný, tak se video normálně spustí.
   * Pokud je offset větší než 1000 a seek není podporovaný, tak dojde k vyvolání výjimky.
   * Pokud je seek podporovaný, pak dojde k přetočení do daného času a následnému "doseeku" o zpozdění způsobené voláním play.
   * Podpora seeku se určuje na základě události seeked tj. pokud se po této události změní currentTime, pak je seek podporovaný.
   * @param {number} offset Posunutí začátku v ms .
   * @param {HTMLVideoElement} viewerFrameEl Zaobalující element elementu, který se má přehrávat.
   * @param {HTMLVideoElement} el Element, který se má přehrát.
   * @private
   * @returns {Promise.<undefined>}
   */
  playSeek(offset, viewerFrameEl, el) {
    let onSeeked;
    const delay = 1000;
    let seekSupported = true;

    const error = new Error('seek not supported, start is too delayed');
    error.name = ErrorName.SeekError;

    const offsetSeconds = offset / 1000;
    return new Promise((resolve, reject) => {
      this.playSeekReject = reject;
      onSeeked = () => {
        const afterSeek = Date.now();
        const seekDelay = afterSeek - beforeSeek;

        // Pokud se currentTime změnil v seeked, tak je seek podporovaný.
        if (el.currentTime !== offsetSeconds) {
          seekSupported = false;
          if (offset + seekDelay > delay) {
            return reject(error);
          }
        }

        return play(seekDelay);
      };

      const play = (seekDelay) => {
        const beforePlay = Date.now();
        return resolve(
          this.play(viewerFrameEl, el).then(() => {
            const afterPlay = Date.now();
            const playDelay = afterPlay - beforePlay;
            if (seekSupported) {
              el.currentTime = (offset + seekDelay + playDelay) / 1000;
            } else if (offset + seekDelay + playDelay > delay) {
              throw error;
            }
          })
        );
      };

      const beforeSeek = Date.now();
      try {
        // Kontrola povolení seekování videa, a kontrola verze chrome.
        // Property controlsList je dostupná od Chrome 58.
        if (this.seekDisable || isNil(el.controlsList)) {
          const seekError = new Error();
          seekError.name = ErrorName.SeekNotSupportedError;
          throw seekError;
        }
        el.addEventListener('seeked', onSeeked);
        el.currentTime = offsetSeconds;
      } catch {
        // Nastavení currentTime může v prohlížečích vyhodit chybu, pokud je readyState < 1
        seekSupported = false;
        play(0);
      }
    }).finally(() => {
      el.removeEventListener('seeked', onSeeked);
    });
  }

  /**
   * Spustí position watchdog
   * @private
   * @returns {undefined}
   */
  startPositionWatchdog() {
    this.positionTimeout = setTimeout(this.positionWatchdog.bind(this), 1000);
  }

  /**
   * Zastaví position watchdog
   * @private
   * @returns {undefined}
   */
  stopPositionWatchdog() {
    clearTimeout(this.positionTimeout);
  }

  /**
   * Ochrana proti zaseknutí přehrávání videa kontrolou currentTime.
   * Slouží eliminaci chyb v Chromiu, kdy se video zastaví (event waiting), ale
   * již poté nepokračuje dále v přehrávání.
   * Pozorováno na Chromiu 90 / Gilbarco, nelze spolehlivě simulovat.
   * @private
   * @returns {undefined}
   */
  positionWatchdog() {
    const el = this.viewerSource.getEl();
    if (el.currentTime === this.positionLastTime) {
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaError
      const error = new Error(`Position timeout: ${el.src}`);
      // Zvoleno nejbližší možné MEDIA_ERR_DECODE
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code#media_error_code_constants
      error.code = 3;
      if (isNil(this.playReject)) {
        this.emitter.emit('error', this.mediaUtil.createError({ error }));
      } else {
        this.playReject(error);
      }
      return;
    }
    this.positionLastTime = el.currentTime;
    this.startPositionWatchdog();
  }

  /** @inheritdoc */
  release() {
    return this.viewerLifeCycle.release(() => {
      const { viewerSource } = this;
      // Určení všech potřebných hodnot.
      const el = viewerSource.getEl();
      const viewerFrame = viewerSource.getFrame();
      // Uvolnění obalujícího elementu.
      viewerFrame.release();
      // Uvolnění zdrojového elementu.
      this.unbindElement(el);
      // Zrušení hodnot v instanci.
      this.scheduleInfo = null;
      this.viewerSource = null;
    });
  }

  /** @inheritdoc */
  seize(scheduleInfo, viewerSource) {
    return this.viewerLifeCycle.seize(() => {
      // Nastavení hodnot v instanci.
      this.scheduleInfo = scheduleInfo;
      this.viewerSource = viewerSource;
      // Určení všech potřebných hodnot.
      return scheduleInfo.getUrl().then((url) => {
        const stretchEnum = scheduleInfo.getStretchEnum();
        const stretchClassName = this.stretchNative.getClassName(stretchEnum);
        const volume = scheduleInfo.getVolume();
        const el = viewerSource.getEl();
        const viewerFrame = viewerSource.getFrame();
        // Příprava obalujícího elementu.
        viewerFrame.seize(scheduleInfo);
        // Zahájí se naslouchání událostem z video elementu.
        this.bindElement(el);
        // Nastaví se CSS třídy.
        el.classList.add('viewer-controller-video', stretchClassName);
        // #505 - webOS 6.0 - nefunkční přehrávání videí
        el.style.visibility = 'hidden';
        // Zablokuje se zpracování zvuku preloadovaného videa.
        // Některé platformy podporují přehrávání pouze jednoho zdroje zvuku
        // a bez muted by mohlo zrušit zvuk u jiného videa.
        el.muted = true;
        if (!isNil(this.poster)) {
          // Některé webové platformy (Mersive) nedodržují standard a mají vlastní poster obrázek, vynutí se prázdný.
          // Nastavení tohoto atributu ale rozhodí přehrávání videa u webOS 4.0,
          // proto je vyčleněno do samostatné implementace.
          el.poster = this.poster;
        }
        // Nastaví se hlasitost, ve které se video později přehraje.
        el.volume = volume;
        // Nastaví se zdroj, což zahájí načítání videa.
        el.src = url;
      });
    });
  }

  /**
   * Uvolní naslouchání událostem z elementu.
   * @param {HTMLVideoElement} el Element, kterému se naslouchá.
   * @private
   * @returns {undefined} Nic.
   */
  unbindElement(el) {
    el.removeEventListener('canplaythrough', this.onCanplaythrough);
    el.removeEventListener('ended', this.onEnded);
    el.removeEventListener('error', this.onError);
    el.removeEventListener('playing', this.onPlaying);
  }
}

export default ViewerControllerVideo;
